import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { BlogBreadcrumbs } from "../components/BlogBreadcrumbs";
import { AdminTable } from "../../../common/adminTable";
import { fetchActivity, fetchMyActivity } from "../activitySlice";
import { inputSx } from "../blogTheme";

const RESOURCES = ["post", "category", "tag", "comment", "media", "redirect", "seo_meta", "content_block"];
const ACTIONS   = ["created", "updated", "deleted", "restored", "published", "unpublished", "archived", "scheduled", "approved", "rejected", "spam", "uploaded", "reordered", "upserted"];
const rowsPerPage = 20;

const adminName = (admin) =>
  admin
    ? admin.firstName && admin.lastName
      ? `${admin.firstName} ${admin.lastName}`
      : admin.username || "—"
    : "—";

export default function ActivityLog() {
  const dispatch = useDispatch();
  const { activities, total, page, loading, error } = useSelector((state) => state.activity);
  const [tab, setTab]           = useState(0);
  const [resource, setResource] = useState("");
  const [action, setAction]     = useState("");
  const [from, setFrom]         = useState(null);
  const [to, setTo]             = useState(null);

  const load = (pg = 1) => {
    const params = { page: pg, limit: rowsPerPage };
    if (resource) params.resource = resource;
    if (action)   params.action   = action;
    if (from)     params.from     = from.toISOString();
    if (to)       params.to       = to.toISOString();
    if (tab === 0) dispatch(fetchActivity(params));
    else           dispatch(fetchMyActivity(params));
  };

  useEffect(() => { load(1); }, [tab, resource, action, from, to]);

  const columns = [
    {
      field: "createdAt",
      headerName: "Date",
      width: 160,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (a) => dayjs(a.createdAt).format("MMM D, YYYY HH:mm"),
    },
    {
      field: "admin",
      headerName: "Admin",
      width: 150,
      renderCell: (a) => adminName(a.admin),
    },
    {
      field: "action",
      headerName: "Action",
      width: 120,
      renderCell: (a) => (
        <Chip
          label={a.action}
          size="small"
          sx={{ fontFamily: "Heebo", bgcolor: "#e8f5f0", color: "#065F46" }}
        />
      ),
    },
    {
      field: "resource",
      headerName: "Resource",
      width: 130,
      renderCell: (a) => a.resource,
    },
    {
      field: "resourceTitle",
      headerName: "Resource Title",
      minWidth: 180,
      cellSx: { maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      renderCell: (a) => a.resourceTitle || "—",
    },
    {
      field: "meta",
      headerName: "Details",
      width: 160,
      renderCell: (a) => {
        const label = a.meta?.title || a.meta?.name || a.meta?.filename;
        return label ? (
          <Chip
            label={label}
            size="small"
            variant="outlined"
            sx={{ maxWidth: 140, fontFamily: "Heebo", borderColor: "#065F46", color: "#362B24" }}
          />
        ) : null;
      },
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <BlogBreadcrumbs items={[{ label: "Blog", href: "/blog" }, { label: "Activity Log" }]} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo", mb: 2 }}>
          Activity Log
        </Typography>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 2,
            "& .MuiTabs-indicator": { bgcolor: "#065F46" },
            "& .MuiTab-root": { fontFamily: "Heebo", color: "#362B24", opacity: 0.6, textTransform: "none" },
            "& .Mui-selected": { color: "#065F46 !important", opacity: 1 },
          }}
        >
          <Tab label="All Activity" />
          <Tab label="My Activity" />
        </Tabs>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <TextField
            label="Resource"
            value={resource}
            onChange={(e) => setResource(e.target.value)}
            size="small"
            select
            sx={{ minWidth: 160, ...inputSx }}
          >
            <MenuItem value="">All Resources</MenuItem>
            {RESOURCES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
          <TextField
            label="Action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            size="small"
            select
            sx={{ minWidth: 160, ...inputSx }}
          >
            <MenuItem value="">All Actions</MenuItem>
            {ACTIONS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <DatePicker
            label="From"
            value={from}
            onChange={setFrom}
            slotProps={{ textField: { size: "small", sx: inputSx } }}
          />
          <DatePicker
            label="To"
            value={to}
            onChange={setTo}
            slotProps={{ textField: { size: "small", sx: inputSx } }}
          />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <AdminTable
          columns={columns}
          rows={activities}
          loading={loading}
          rowKey="_id"
          emptyText="No activity found"
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={total}
          onPageChange={(pg) => load(pg)}
        />
      </Box>
    </LocalizationProvider>
  );
}
